/**
 * Apple Pay availability at checkout.
 *
 * ## Why this is a flag and not just "on"
 *
 * Apple Pay is a Stripe feature, and Defera Capital moved to NMI on
 * 2026-06-18 (`platform_settings.defera_capital_processor = 'nmi'`). The
 * Stripe Apple Pay path was written for Capital-on-Stripe and has had zero
 * traffic since that day — not because anything broke, but because the rail
 * it hangs off stopped being used. Everything else is healthy: `defera.app`
 * is a registered Apple Pay domain on the live Stripe account, and
 * `/.well-known/apple-developer-merchantid-domain-association` serves 200.
 *
 * So Capital checkout now runs TWO processors on one screen: the NMI card
 * form (default) with a Stripe Apple Pay button above it. Choosing Apple Pay
 * routes that booking down the Capital-on-Stripe path — the same code that
 * ran 48 trips in May–June — while the card form stays on NMI.
 *
 * That is deliberately the shape of the 5VOPA3 incident (a customer
 * submitting through the wrong processor while the toggle resolved), which
 * is why it ships behind a flag, defaults OFF, and why the two submit paths
 * are kept on separate component instances with independent locks.
 *
 * Turn it on with `VITE_APPLE_PAY_ENABLED=true`. Verify on a real device —
 * the Apple Pay sheet cannot be exercised in a headless browser, so no
 * automated test can prove the tap works end to end.
 */
export const APPLE_PAY_ENABLED = import.meta.env.VITE_APPLE_PAY_ENABLED === 'true';

/**
 * Rails where the Apple Pay button is offered.
 *
 * `capital` is live: it has a real deposit, so the Apple Pay sheet shows a
 * real amount and the existing PaymentRequest flow applies unchanged.
 *
 * `zero` is NOT here yet, on purpose. Defera Zero is 0% down — nothing is
 * charged today — and Review.jsx passes the Zero form `installmentCount: 0`
 * and `installmentAmountCents: 0`, so there is no honest figure to put in an
 * Apple Pay sheet. The correct integration is `ExpressCheckoutElement` in
 * `mode: 'setup'`, which collects a card for future use and needs no amount
 * at all; that is a separate change, not a flag flip here.
 */
export const APPLE_PAY_RAILS = Object.freeze(['capital']);

export function applePayAvailableForRail(rail) {
  return APPLE_PAY_ENABLED && APPLE_PAY_RAILS.includes(rail);
}
